/**
 * decisionAccount - which investor account an admin decision is about
 * (person-first WP6b; api WP5 at d8a179f3).
 *
 * THE SERVER'S RULE (api app/common/accountSelection.js): a decision names its
 * account with `investor_account_id` or `account_kind` (INDIVIDUAL | ENTITY,
 * readSelector). With neither, a user with ONE selectable account is decided
 * on that one; a user with TWO (the person, plus an ACTIVE ENTITY row) is
 * refused with 400 ACCOUNT_KIND_REQUIRED and nothing is written (decide). The
 * admin never decides the account from entity_type or the comp_* flags (P1).
 *
 * ONE CONTROL, ONE COPY on every decision screen: askDecisionAccount, a radio
 * question "Personal" / "Company", none preselected, required.
 *
 * SEND ONCE MORE, NEVER TWICE: sendNamingAccountOnce sends the body as the
 * screen built it. When the server answers 400 ACCOUNT_KIND_REQUIRED to a body
 * that named no account, the question is asked and the same body is sent once
 * more with the chosen `account_kind`. Every other answer, and any answer to
 * the second send, is the caller's to show as given.
 *
 * AN ACCREDITATION REQUEST'S ACCOUNT (requestAccountState): a request row that
 * carries its own `investor_account_id` (stamped, api M7) is decided by that
 * account (accreditedInvestor.controller requestAccountOf); a selector could
 * only conflict with it, so a stamped request is never asked. A row whose
 * investor_account_id is null (written before M7) or that does not carry the
 * field (the list read selects no such column) cannot tell.
 */
import Swal from "sweetalert2";
import { apiErrorCode, apiStatus } from "./apiError";

/** account_kind values (api accountSelection.KINDS). */
export const ACCOUNT_KINDS = Object.freeze({ INDIVIDUAL: "INDIVIDUAL", ENTITY: "ENTITY" });
const CHOICES = Object.freeze({ [ACCOUNT_KINDS.INDIVIDUAL]: "Personal", [ACCOUNT_KINDS.ENTITY]: "Company" });

export const ACCOUNT_QUESTION = "Which account is this decision for?";
export const ACCOUNT_CHOICE_REQUIRED = "Choose Personal or Company.";
export const ACCOUNT_KIND_REQUIRED = "ACCOUNT_KIND_REQUIRED";

const kindOf = (value) => (Object.prototype.hasOwnProperty.call(CHOICES, value) ? value : null);

/**
 * askDecisionAccount() -> "INDIVIDUAL" | "ENTITY" | null (cancelled).
 * Only one of the two kinds is ever returned, whatever the dialog hands back.
 */
export async function askDecisionAccount() {
  const result = await Swal.fire({
    title: ACCOUNT_QUESTION,
    input: "radio",
    inputOptions: { ...CHOICES },
    inputValidator: (value) => (kindOf(value) ? undefined : ACCOUNT_CHOICE_REQUIRED),
    showCancelButton: true,
    confirmButtonText: "Continue",
  });
  return result && result.isConfirmed ? kindOf(result.value) : null;
}

/** Is this rejection the server's 400 ACCOUNT_KIND_REQUIRED (two accounts, none named)? */
export function isAccountKindRequired(err) {
  return apiStatus(err) === 400 && apiErrorCode(err) === ACCOUNT_KIND_REQUIRED;
}

export const REQUEST_ACCOUNT = Object.freeze({
  STAMPED: "STAMPED",
  UNSTAMPED: "UNSTAMPED",
  UNKNOWN: "UNKNOWN",
});

/**
 * requestAccountState(row) -> STAMPED (a positive integer investor_account_id),
 * UNSTAMPED (investor_account_id null: a request from before M7), or UNKNOWN
 * (the row does not carry the field, or carries something that is not an id).
 */
export function requestAccountState(row) {
  if (!row || typeof row !== "object" || !Object.prototype.hasOwnProperty.call(row, "investor_account_id")) {
    return REQUEST_ACCOUNT.UNKNOWN;
  }
  const value = row.investor_account_id;
  if (value === null) return REQUEST_ACCOUNT.UNSTAMPED;
  const raw = typeof value === "number" || typeof value === "string" ? String(value).trim() : "";
  return /^\d+$/.test(raw) && Number(raw) > 0 ? REQUEST_ACCOUNT.STAMPED : REQUEST_ACCOUNT.UNKNOWN;
}

/**
 * sendNamingAccountOnce(send, body, { mayAsk, beforeAsk, afterAsk })
 *   -> { response } | { response, accountKind } | { cancelled: true }; throws the refusal as given.
 * send(body) is the screen's own request. mayAsk false (a stamped request)
 * never asks. beforeAsk runs before the question (drop a loader, close a
 * modal), afterAsk after a choice, before the second send.
 */
export async function sendNamingAccountOnce(send, body, { mayAsk = true, beforeAsk, afterAsk } = {}) {
  try {
    return { response: await send(body) };
  } catch (err) {
    const named = Object.prototype.hasOwnProperty.call(body || {}, "account_kind");
    if (!mayAsk || named || !isAccountKindRequired(err)) throw err;
    if (beforeAsk) beforeAsk();
    const accountKind = await askDecisionAccount();
    if (!accountKind) return { cancelled: true };
    if (afterAsk) afterAsk();
    return { response: await send({ ...body, account_kind: accountKind }), accountKind };
  }
}
